import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plane, Trash2, LogIn, Save } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api } from "../lib/api";

const today = () => new Date().toLocaleDateString("en-CA");
const addDay = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x.toLocaleDateString("en-CA"); };
const diff = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);

export const leaveState = (r) => {
  if (r.return_date) return { k: "returned", label: `باشر ${r.return_date.slice(5)}`, cls: "text-emerald-700" };
  if (!r.start_date || !r.end_date) return { k: "none", label: "بدون تواريخ", cls: "text-slate-400" };
  const t = today(), due = addDay(r.end_date, 1);
  if (t < r.start_date) return { k: "upcoming", label: `يبدأ ${r.start_date.slice(5)}`, cls: "text-sky-600" };
  if (t <= r.end_date) return { k: "on", label: `يعود ${due.slice(5)}`, cls: "text-amber-600" };
  return { k: "late", label: `متأخر ${diff(due, t)}ي`, cls: "text-rose-600 font-bold" };
};

const Row = ({ label, children, tid }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0 text-sm" data-testid={tid}>
    <span className="text-slate-500">{label}</span><span className="tabular-nums text-slate-800" dir="auto">{children}</span>
  </div>
);

export const LeaveDetailDialog = ({ leave, canEdit, onClose, onChanged, onFlight, onDelete }) => {
  const [f, setF] = useState({ start_date: leave.start_date || "", end_date: leave.end_date || "", return_date: leave.return_date || "" });
  const [ret, setRet] = useState(today());
  const [busy, setBusy] = useState(false);
  const st = leaveState(leave);
  const days = f.start_date && f.end_date && f.end_date >= f.start_date ? diff(f.start_date, f.end_date) + 1 : 0;
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذر الحفظ"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md font-body" dir="rtl" data-testid="leave-detail-dialog">
        <DialogHeader className="text-right"><DialogTitle className="font-heading text-lg">{leave.employee_name}</DialogTitle></DialogHeader>
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-3">
          <Row label="المعرف / الإقامة">{leave.identifier || "—"}</Row>
          <Row label="الفرع">{leave.branch || "—"}</Row>
          <Row label="بداية الإجازة" tid="leave-detail-start">{leave.start_date || "—"}</Row>
          <Row label="نهاية الإجازة" tid="leave-detail-end">{leave.end_date || "—"}</Row>
          <Row label="عدد الأيام">{leave.days || "—"}{leave.unpaid_days ? ` (${leave.unpaid_days} بدون راتب)` : ""}</Row>
          <Row label="موعد العودة المتوقع" tid="leave-detail-due">{leave.end_date ? addDay(leave.end_date, 1) : "—"}</Row>
          {leave.exit_date && <Row label="تاريخ الخروج (مقيم)">{leave.exit_date}</Row>}
          <Row label="العودة الفعلية" tid="leave-detail-return">{leave.return_date || "لم يباشر بعد"}</Row>
          <div className="flex justify-between py-1.5 text-sm"><span className="text-slate-500">الحالة</span><span className={st.cls} data-testid="leave-detail-state">{st.label}</span></div>
        </div>
        {canEdit && !leave.return_date && (
          <div className="flex items-end gap-2 rounded-xl border border-emerald-200 bg-emerald-50/50 p-3">
            <label className="flex-1"><span className="text-[11px] text-slate-500">تاريخ المباشرة الفعلي</span>
              <Input type="date" value={ret} min={leave.start_date} onChange={(e) => setRet(e.target.value)} className="h-8 text-sm" data-testid="leave-return-date" /></label>
            <Button size="sm" disabled={busy || !ret} data-testid="leave-return-btn" className="gap-1 bg-emerald-600 hover:bg-emerald-700"
              onClick={() => run(() => api.leaveReturn(leave.id, ret), "تم تسجيل المباشرة")}><LogIn className="h-4 w-4" /> باشر العمل</Button>
          </div>
        )}
        {canEdit && (
          <div className="space-y-2 rounded-xl border border-slate-200 p-3">
            <p className="text-xs font-semibold text-slate-600">تعديل التواريخ</p>
            <div className="grid grid-cols-2 gap-2">
              <label><span className="text-[11px] text-slate-500">من</span><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className="h-8 text-sm" data-testid="leave-edit-start" /></label>
              <label><span className="text-[11px] text-slate-500">إلى</span><Input type="date" value={f.end_date} min={f.start_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className="h-8 text-sm" data-testid="leave-edit-end" /></label>
            </div>
            <label className="block"><span className="text-[11px] text-slate-500">تاريخ العودة الفعلي (اتركه فارغاً إن لم يرجع)</span>
              <Input type="date" value={f.return_date} onChange={(e) => setF({ ...f, return_date: e.target.value })} className="h-8 text-sm" data-testid="leave-edit-return" /></label>
            {days > 0 && <p className="text-xs text-blue-700" data-testid="leave-edit-days">{days} يوم</p>}
            <Button size="sm" disabled={busy || !days} data-testid="leave-edit-save" className="w-full gap-1 bg-blue-600 hover:bg-blue-700"
              onClick={() => run(() => api.editLeave(leave.id, f), "تم حفظ التعديل")}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ</Button>
          </div>
        )}
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1 gap-1 text-sky-700" data-testid="leave-detail-flight" onClick={onFlight}><Plane className="h-4 w-4" /> حجز تذكرة</Button>
            <Button variant="outline" size="sm" className="gap-1 text-rose-600" data-testid="leave-detail-delete" onClick={onDelete}><Trash2 className="h-4 w-4" /> حذف</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
